import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * EditUserModal component for editing user information in the admin panel.
 * @param {Object} props - Component props
 * @param {Object} props.user - User object to edit
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {Function} props.onUserUpdated - Function to call when user is successfully updated
 */
const EditUserModal = ({ user, isOpen, onClose, onUserUpdated }) => {
  const { theme } = useTheme();
  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: `1px solid ${theme.inputBorder ?? theme.border}`,
    borderRadius: '4px',
    backgroundColor: theme.inputBg ?? theme.cardBg ?? theme.secondaryBg,
    color: theme.primaryText,
  };
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    is_premium: false,
    is_admin: false,
    email_subscribed: true,
    email_results_subscribed: true,
    member_since: "",
    last_login: "",
    login_count: 0,
    premium_since: "",
    premium_expires: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        is_premium: user.is_premium || false,
        is_admin: user.is_admin || false,
        email_subscribed: user.email_subscribed ?? true,
        email_results_subscribed: user.email_results_subscribed ?? true,
        member_since: user.member_since ? user.member_since.split('T')[0] : "",
        last_login: user.last_login ? user.last_login.split('T')[0] : "",
        login_count: user.login_count || 0,
        premium_since: user.premium_since ? user.premium_since.split('T')[0] : "",
        premium_expires: user.premium_expires ? user.premium_expires.split('T')[0] : ""
      });
      setError("");
      setSuccess("");
    }
  }, [user, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.put(`/admin/users/${user.id}`, formData);
      setSuccess("User updated successfully!");
      if (onUserUpdated) {
        onUserUpdated(response.data.user);
      }
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: theme.cardBg ?? theme.secondaryBg,
        color: theme.primaryText,
        borderRadius: '8px',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, color: theme.primaryText }}>Edit User: {user?.email}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
              color: theme.primaryText
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: theme.error ? `${theme.error}33` : 'rgba(245, 101, 101, 0.2)',
            color: theme.error ?? '#f56565',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: theme.success ? `${theme.success}33` : 'rgba(72, 187, 120, 0.2)',
            color: theme.success ?? '#48bb78',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
                Member Since
              </label>
              <input
                type="date"
                name="member_since"
                value={formData.member_since}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
                Last Login
              </label>
              <input
                type="date"
                name="last_login"
                value={formData.last_login}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
              Login Count
            </label>
            <input
              type="number"
              name="login_count"
              value={formData.login_count}
              onChange={handleInputChange}
              min="0"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
                Premium Since
              </label>
              <input
                type="date"
                name="premium_since"
                value={formData.premium_since}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: theme.primaryText }}>
                Premium Expires
              </label>
              <input
                type="date"
                name="premium_expires"
                value={formData.premium_expires}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="is_premium"
                checked={formData.is_premium}
                onChange={handleInputChange}
                style={{ marginRight: '0.5rem', accentColor: theme.primary }}
              />
              <label style={{ fontWeight: 'bold', color: theme.primaryText }}>
                Premium User
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="is_admin"
                checked={formData.is_admin}
                onChange={handleInputChange}
                style={{ marginRight: '0.5rem', accentColor: theme.primary }}
              />
              <label style={{ fontWeight: 'bold', color: theme.primaryText }}>
                Admin User
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 'bold', color: theme.primaryText }}>
              Email Subscriptions
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="email_subscribed"
                  checked={formData.email_subscribed}
                  onChange={handleInputChange}
                  style={{ marginRight: '0.5rem', accentColor: theme.primary }}
                />
                <label style={{ color: theme.primaryText, fontSize: '0.9rem' }}>
                  Daily Picks
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="email_results_subscribed"
                  checked={formData.email_results_subscribed}
                  onChange={handleInputChange}
                  style={{ marginRight: '0.5rem', accentColor: theme.primary }}
                />
                <label style={{ color: theme.primaryText, fontSize: '0.9rem' }}>
                  Results
                </label>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.secondaryBg ?? theme.tertiaryBg,
                color: theme.primaryText,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: loading ? theme.tertiaryBg : (theme.primary ?? '#4299e1'),
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal; 